import {
    Box,
    Chip,
    List,
    ListItem,
    ListItemText,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetProfileCompanyInfoQuery } from '../../../../app/Slices/SevenDayApiSlice';
import { Job, ProviderType } from '../../../../app/Slices/SevenDayApiTypes';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import UserRole from '../../../../shared/AuthRoles';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import GenericTable from '../../../shared/GenericTable';
import { BookingSetMenu } from '../../../types/set-menu';

export type Status =
    | 'pending'
    | 'accepted'
    | 'declined'
    | 'cancelled'
    | 'completed'
    | 'pendingCompletion'
    | undefined;

interface JobListProps {
    jobs: (Job | BookingSetMenu)[];
}

const JobList = ({ jobs }: JobListProps) => {
    const loggedInUser = UserRole();

    const { data: companyDetails } = useGetProfileCompanyInfoQuery(
        `owners/${loggedInUser?.company}`,
        { skip: !loggedInUser?.company },
    );

    const isMobile = useMediaQuery('(max-width: 770px)');
    const [searchParams, setSearchParams] = useSearchParams();

    const handleView = (job: Job | BookingSetMenu) => {
        const next = new URLSearchParams(searchParams);
        next.set('selectedJob', String(job.id));
        next.set('provider-type', String(job.providerType));
        setSearchParams(next);
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Job Id',
            size: 90,
            mantineTableHeadCellProps: {
                style: { minWidth: 90, maxWidth: 90, width: 90 },
            },
            mantineTableBodyCellProps: {
                style: {
                    minWidth: 70,
                    maxWidth: 80,
                    width: 80,
                    padding: '0 4px',
                },
            },
            Cell: ({ cell }) => (
                <Typography>{`#${cell.getValue<string>()}`}</Typography>
            ),
        },
        {
            accessorKey: 'providerType',
            header: 'Provider',
            size: 90,
            accessorFn: (row) =>
                row?.providerType === ProviderType.Contractor
                    ? 'Contractor'
                    : `${companyDetails?.name} Services`,
        },
        {
            accessorKey: 'contractor.name',
            header: 'Contractor',
            accessorFn: (row) =>
                row?.providerType === ProviderType.Contractor
                    ? row?.contractor?.name
                    : '-',
        },
        {
            accessorKey: 'recurringId',
            header: 'Recurring Id',
            Cell: ({ row, cell }) => {
                return (
                    <List sx={{ p: 0 }}>
                        <ListItem sx={{ p: 0 }}>
                            <ListItemText
                                primary={
                                    <Typography>
                                        {row?.original.recurringId
                                            ? `#${row?.original.recurringId}`
                                            : '-'}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {row?.original?.recurringId
                                            ? `This job is apart of a recurring job: ${row?.original?.recurringId}`
                                            : ''}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },

        {
            accessorKey: 'bookingId',
            header: 'Reservation',
            mantineTableBodyCellProps: { style: { padding: 0 } },
            Cell: ({ row, cell }) => {
                return (
                    <List sx={{ p: 0 }}>
                        <ListItem sx={{ p: 0 }}>
                            <ListItemText
                                primary={
                                    <Typography>
                                        {`#${row.original?.booking?.id}`}
                                    </Typography>
                                }
                                secondary={
                                    <Typography
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {`${row.original?.module?.name}`}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    </List>
                );
            },
        },

        {
            accessorKey: 'jobDate',
            header: 'Due Date',

            Cell: ({ cell }) => (
                <Typography>
                    {new Date(cell.getValue<string>()).toLocaleDateString(
                        'en-AU',
                    )}
                </Typography>
            ),

            mantineTableHeadCellProps: {
                style: { minWidth: 110, maxWidth: 110, width: 110 },
            },
            mantineTableBodyCellProps: {
                style: {
                    minWidth: 70,
                    maxWidth: 80,
                    width: 80,
                    padding: '0 4px',
                },
            },
        },
        {
            accessorKey: 'services',
            header: 'Services',
            Cell: ({ cell, row }) => {
                const value = cell.getValue<any[]>();
                const remainders = isMobile ? value?.length : value?.length - 2;
                const isOwnerJob =
                    row.original.providerType === ProviderType.Owner;
                const ownerJob = isOwnerJob
                    ? `${row.original.serviceType} set menu`
                    : undefined;

                return (
                    <Box sx={{ display: 'flex', overflow: 'clip', gap: 0.5 }}>
                        {isOwnerJob ? (
                            <CustomChip
                                variant='filled'
                                sx={{
                                    backgroundColor: 'lightgrey',
                                }}
                                label={camelCaseToTitleCase(ownerJob ?? '')}
                            />
                        ) : (
                            value
                                ?.slice(0, isMobile ? 1 : 2)
                                .map((service: string, index: number) => (
                                    <CustomChip
                                        key={index}
                                        variant='filled'
                                        sx={{
                                            backgroundColor: 'lightgrey',
                                        }}
                                        label={camelCaseToTitleCase(service)}
                                    />
                                ))
                        )}
                        {remainders > 0 && (
                            <Chip
                                sx={{ height: 'auto', fontSize: '10px' }}
                                label={`+ ${remainders}`}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomChip
                        variant='outlined'
                        color={
                            ['pending', 'pendingBooking'].includes(value)
                                ? 'blue'
                                : value === 'declined'
                                  ? 'gray'
                                  : ['accepted', 'active'].includes(value)
                                    ? 'yellow'
                                    : value === 'completed'
                                      ? 'green'
                                      : value === 'pendingCompletion'
                                        ? 'yellow'
                                        : 'red'
                        }
                        label={camelCaseToTitleCase(value).toUpperCase()}
                    />
                );
            },
        },
    ];

    return (
        <Box sx={{ width: '100%', pt: '20px' }}>
            <GenericTable
                columns={columns}
                data={jobs}
                tableName='Job-List'
                loading={!jobs}
                onRowClick={(row) => handleView(row)}
                cursor='pointer'
            />
        </Box>
    );
};

export default JobList;
